import { getGewerk } from './getGewerk';
import { IDs } from '../../helpers/IDs';
import { lazyInject } from '../../services/inversify';
import * as Moment from 'moment';
import { RoutingIDs } from '../..//helpers/RoutingIDs';
import { StructureService } from '../../services/structure_service';
import { Validierung } from '../../bereiche/datenauswertung/elements/Validierung';

export class EnergiedatenPlausibel {

    public constructor(data: any, jahr?: number) {
        this.data = data || {};
        this.jahr = jahr || null;
        this.init();
    }

    @lazyInject(StructureService) private structureService: StructureService;

    private data: any;
    private eigenstromTypen: any = {};
    private eintraege: any[] = [];
    private isGefuehrterModus: boolean;
    private jahr: number;
    private standorte: any[] = [];
    private typ: string = 'energie';

    public getData(): any[] {
        return this.eintraege;
    }

    private getBWL(): void {
        const entity: string = IDs.ENT_BETRIEBSDATEN || null;
        const energiedaten: any[] = this.data[entity] || [];
        const data: any[] = this.getJahresdaten(energiedaten) || [];
        this.getStandortdaten(data, entity);
    }

    private getEigenstrom(): void {
        const entity: string = IDs.ENT_ENERGIEDATEN_EIGENSTROM || null;
        const energiedaten: any[] = this.data[entity] || [];
        const data: any[] = this.getJahresdaten(energiedaten) || [];
        this.setEigenstromTypen();
        this.getStandortdaten(data, entity);
    }

    private getJahresdaten(data: any[]): any[] {
        return (data && this.jahr)
            ? data.filter((item: any) => item.jahr === this.jahr)
            : data;
    }

    private getKraftstoffe(): void {
        const energiedaten: any[] = this.data[IDs.ENT_ENERGIEDATEN_KRAFTSTOFFE] || [];
        const data: any[] = this.getJahresdaten(energiedaten) || [];
        this.getStandortdaten(data, IDs.ENT_ENERGIEDATEN_KRAFTSTOFFE);
    }

    private getStandortdaten(data: any[], entity: string): void {
        const eintrag: any = { entity: IDs.ENT_STANDORT, typ: this.typ };
        const structure: any = this.structureService.getStructureByKey(IDs.MOD_ETOOL, entity) || {};
        let level: string = null;
        let text: string = null;

        Object.keys(this.standorte).forEach((key: any, index: number) => {
            const standort: any = this.standorte[key] || {};
            const name: string = standort.name || null;
            const tid: number = standort.tid || null;
            const routing: any = { [IDs.ENT_STANDORT]: tid };
            const standortVar: string = this.isVeranstaltung(tid) ? 'die Veranstaltung' : 'den Standort';
            const dataFiltered = data.filter((item: any) => (item.standort_tid_orig || item.standort_tid) === tid);
            const validation: any = new Validierung(this.data, tid, [{ titel: structure.TITEL, entity }], 'jahr', null, null, this.jahr).getData();
            const path: any = this.isGefuehrterModus
                ? RoutingIDs.ROUTE_DATENEINGABE
                : {
                    ...RoutingIDs.ROUTE_DATENEINGABE_ENERGIEDATEN_KOMPLEX,
                    [RoutingIDs.TABS_DATENEINGABE_ENERGIEDATEN_KOMPLEX]: index,
                    terminateOn: RoutingIDs.TABS_DATENEINGABE_ENERGIEDATEN_KOMPLEX
                };
            const setEintrag = (validation: any): void => {
                if (this.isVeranstaltung(tid) && !this.isInRangeJahr(tid, this.jahr)) {
                    return;
                }
                this.eintraege.push({
                    ...eintrag,
                    level,
                    routing: { jahr: this.jahr, ...routing, [entity]: dataFiltered.map((item: any) => item.tid), path, entities: [entity] },
                    text,
                    tid,
                    validation
                });
            };
            if (entity !== IDs.ENT_ENERGIEDATEN_EIGENSTROM) {
                if (dataFiltered.length) {
                    const hasErrors: boolean = this.hasErrors(validation, entity);
                    level = hasErrors ? 'error' : 'ok';
                    text = `${structure.TITEL} für ${standortVar} ${name} wurden${hasErrors ? ' fehlerhaft' : ''} angegeben`;
                } else {
                    level = 'error';
                    text = `${structure.TITEL} für ${standortVar} ${name} wurden nicht angegeben`;
                }
                setEintrag(validation);
            } else {
                (this.eigenstromTypen[tid] || []).forEach((typ: any) => {
                    const validationEigenstrom: any = { [this.jahr]: { } };

                    if (dataFiltered.filter((item: any) => {
                        if (item.anlagen_eigenstrom_typ_tid_orig === typ.tid) {
                            const item2: any = (((validation || {})[this.jahr] || {})[entity] || {})[item.tid];

                            if (item2) {
                                validationEigenstrom[this.jahr][entity] = { ...validationEigenstrom[this.jahr][entity], [item.tid]: item2 };
                            }
                            return true;
                        }
                        return false;
                    }).length) {
                        const hasErrors: boolean = this.hasErrors(validationEigenstrom, entity);
                        level = hasErrors ? 'error' : 'ok';
                        text = `${structure.TITEL} [${typ.name || ''}] für den Standort ${name} wurden${hasErrors ? ' fehlerhaft' : ''} angegeben`;
                    } else {
                        level = 'error';
                        text = `${structure.TITEL} [${typ.name || ''}] für den Standort ${name} wurden nicht angegeben`;
                    }
                    setEintrag(validationEigenstrom);
                });
            }

            if (entity === IDs.ENT_ENERGIEDATEN_WAERME) {
                this.getWaermeEnergietraeger(data, tid);
            }
        });
    }

    private getStrom(): void {
        const energiedaten: any[] = this.data[IDs.ENT_ENERGIEDATEN_STROM] || [];
        const data: any[] = (this.getJahresdaten(energiedaten) || []).filter((item: any) => !item.parent_tid);
        const structure = this.structureService.getStructureByKey(IDs.MOD_ETOOL, IDs.ENT_ENERGIEDATEN_STROM);
        this.getStandortdaten(data, IDs.ENT_ENERGIEDATEN_STROM)

        data.forEach((strom: any) => {
            const jahr: number = strom.jahr || null;
            const standort_tid: number = strom.standort_tid_orig || null;
            const standortVar: string = this.isVeranstaltung(standort_tid) ? 'Die Veranstaltung' : 'Der Standort';
            const tid: number = strom.tid || null;
            const eintrag: any = { entity: IDs.ENT_ENERGIEDATEN_STROM, tid, typ: this.typ };
            const routing: any = { [IDs.ENT_ENERGIEDATEN_STROM]: tid };
            let level: string = null;
            let text: string = null;

            if (!jahr) {
                level = 'error';
                text = `Das Jahr für ${structure.TITEL} wurde nicht angegeben`;
                this.eintraege.push({
                    ...eintrag, level, routing: {...routing, path: RoutingIDs.ENTITY_ROUTES[IDs.ENT_ENERGIEDATEN_STROM] }, text
                });
            }

            if (!standort_tid) {
                level = 'error';
                text = `${standortVar} für ${structure.TITEL} ${jahr} wurde nicht angegeben`;
                this.eintraege.push({
                    ...eintrag, level, routing: {...routing, path: RoutingIDs.ENTITY_ROUTES[IDs.ENT_ENERGIEDATEN_STROM] }, text
                });
            }
        });
    }

    private getWaerme(): void {
        const energiedaten: any[] = this.data[IDs.ENT_ENERGIEDATEN_WAERME] || [];
        const data: any[] = (this.getJahresdaten(energiedaten) || []).filter((item: any) => !item.parent_tid);
        this.getStandortdaten(data, IDs.ENT_ENERGIEDATEN_WAERME);
    }

    private getWaermeEnergietraeger(data: any[], standort: number): void {
        const entity: string = IDs.ENT_ENERGIEDATEN_WAERME || null;
        const entityStandort: string = IDs.ENT_STANDORT_ENERGIETRAEGER || null;
        const eintrag: any = { entity: entityStandort, typ: this.typ };
        const energietraeger: number[] = data.map((item: any) => item.energietraeger_tid_orig);
        const standortEnergietraeger: any[] = this.data[entityStandort] || [];
        let level: string = null;
        let text: string = null;

        const setEintrag = (item: any): void => {
            const standortItem: string = item.standort_tid;
            const energietraegerItem: string = item.energiedaten_waerme_energietraeger_tid;
            const energietraegerTidItem: number = item.energiedaten_waerme_energietraeger_tid_orig || null;
            const routing: any = { [entityStandort]: standort };
            let energiedaten: any[] = [];

            if (energietraegerTidItem && energietraeger.indexOf(energietraegerTidItem) === -1) {
                level = 'error';
                text = `Der Verbrauch für den Energieträger ${energietraegerItem} des Standortes ${standortItem} wurden nicht angegeben`;
            } else {
                level = 'ok';
                text = `Der Verbrauch für den Energieträger ${energietraegerItem} des Standortes ${standortItem} wurden angegeben`;
                energiedaten = data
                    .filter((energiedatenItem: any) => (
                        energiedatenItem.energietraeger_tid_orig
                        && +energiedatenItem.energietraeger_tid_orig === energietraegerTidItem
                    ))
                    .map((energiedatenItem: any) => energiedatenItem.tid);
            }
            this.eintraege.push({
                ...eintrag,
                level,
                routing: { ...routing, [entity]: energiedaten, path: RoutingIDs.ENTITY_ROUTES[entity] },
                text,
                tid: standort
            });
        };
        standortEnergietraeger
            .filter((item: any) => (
                item.standort_tid_orig
                && +item.standort_tid_orig === standort
            ))
            .map((item: any) => setEintrag(item));
    }

    private getWasser(): void {
        const energiedaten: any[] = this.data[IDs.ENT_ENERGIEDATEN_WASSER] || [];
        const data: any[] = this.getJahresdaten(energiedaten) || [];
        this.getStandortdaten(data, IDs.ENT_ENERGIEDATEN_WASSER);
    }

    private hasErrors(validation: any, entity: string): boolean {
        const data: any = ((validation || {})[this.jahr] || {})[entity] || {};

        return Object.keys(data).some((id: string) => Object.keys(data[id]).some((key: string) => data[id][key].type === 'error'));
    }

    private init(): void {
        const gewerk = getGewerk(this.data);
        const hasGewerke: boolean = gewerk && gewerk > 0;
        const konfiguration: any = (this.data[IDs.ENT_KONFIGURATION] || [])[0] || {};
        this.isGefuehrterModus = konfiguration.gefuehrter_modus_orig === 1;
        this.setStandorte();
        this.getStrom();

        if (konfiguration.daten_erneuerbare_energien_orig === 1) {
            this.getEigenstrom();
        }
        this.getWaerme();

        if (hasGewerke && konfiguration.daten_fahrzeuge_orig === 1) {
            this.getKraftstoffe();
        }
        this.getWasser();
        this.getBWL();
    }

    private isInRangeJahr(tid: number, jahr: number): boolean {
        const standort: any = this.standorte[tid] || {};
        const max: number = Moment.unix(standort.datum_bis).year();
        const min: number = Moment.unix(standort.datum_von).year();

        return jahr >= min && jahr <= max;
    }

    private isVeranstaltung(tid: number): boolean {
        return [5, 6].indexOf((this.standorte[tid] || {}).standort_typ_tid_orig) !== -1;
    }

    private setEigenstromTypen(): void {
        (this.data[IDs.ENT_STANDORT] || []).forEach((standort: any) => {
            const eigenstrom: number[] = (this.data[IDs.ENT_ANLAGEN_EIGENSTROM] || []).map((item: any) =>
                (item.standort_tid_orig || null) === standort.tid && item.anlagen_eigenstrom_typ_tid_orig
                    ? item.anlagen_eigenstrom_typ_tid_orig : null
            );
            this.eigenstromTypen[standort.tid] = (this.data[IDs.ENT_ANLAGEN_EIGENSTROM_TYPEN] || [])
                .filter((typ: any) => (typ.typ || null) === 'S' && eigenstrom.indexOf(typ.tid) !== -1);
        });
    }

    private setStandorte(): void {
        const standorte: any = {};

        (this.data[IDs.ENT_STANDORT] || []).forEach((standort: any) => {
            if (
                standort.energiedaten_erfassen_orig === 1
                && ([5, 6].indexOf(standort.standort_typ_tid_orig) === -1 || ((this.data[IDs.ENT_KONFIGURATION] || [])[0] || {}).veranstaltungen_orig === 1)
            ) {
                standorte[standort.tid] = standort;
            }
        });
        this.standorte = standorte;
    }
}
